import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Box,
  InputAdornment,
  TextField,
  CircularProgress,
} from "@mui/material";
import { AccountCircle, ExpandMore, Search, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";
import CCBAppBarLogo from "../../assets/Nav Bar.png";
import "../../components/connect/connect.css";
import "./InstanceList.css";

const InstanceListPage = () => {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingInstanceId, setLoadingInstanceId] = useState(null);

  const raw = localStorage.getItem("ListInstancesResponse");
  const listData = raw ? JSON.parse(raw) : {};
  const instances = listData.instances || [];
  const awsAccount = listData.awsAccount || "";

  const username = localStorage.getItem("username") || "User";

  const handleProfileClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => { setAnchorEl(null); setShowProfileDetails(false); };
  const handleLogOut = () => { localStorage.clear(); navigate("/"); };

  // Format ISO date string to a readable local date-time
  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  // Filter by alias or ID
  const filtered = instances.filter((inst) => {
    const q = searchQuery.toLowerCase();
    return (
      (inst.InstanceAlias || "").toLowerCase().includes(q) ||
      (inst.InstanceId || "").toLowerCase().includes(q)
    );
  });

  const handleSelectInstance = async (inst) => {
    if (loadingInstanceId) return;

    localStorage.setItem("SelectedInstance", JSON.stringify(inst));

    // Set legacy shared variables for the rest of the application components:
    localStorage.setItem("Region", inst.Region || "");
    localStorage.setItem("InstanceId", inst.InstanceId || "");
    localStorage.setItem("EncryptedId", inst.EncryptedInstanceId || "");
    if (inst.Email) {
      localStorage.setItem("Email", inst.Email);
    }

    setLoadingInstanceId(inst.InstanceId);

    try {
      // API 1: S3 Bucket Creation for Prompts
      const s3BucketPromise = axiosInstance.post(
        endpoints.s3Prompts.createBucket,
        {
          region: inst.Region,
          awsAccount: awsAccount,
        }
      );

      // API 2: Create Audit Log Tables
      const auditLogPromise = axiosInstance.post(
        endpoints.instanceapihit.instanceUser,
        {
          InstanceAlias: inst.InstanceAlias,
        }
      );

      await Promise.allSettled([s3BucketPromise, auditLogPromise]);
      navigate("/connect-page");
    } catch (error) {
      console.error("Error hitting selection APIs:", error);
      setLoadingInstanceId(null);
    }
  };

  return (
    <>
      {/* ===== NAVBAR ===== */}
      <AppBar
        position="static"
        elevation={0}
        sx={{ backgroundColor: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}
      >
        <Toolbar className="appbar-connectadmin-container">
          <img src={CCBAppBarLogo} alt="Logo" className="connect-navbar-logo" />
          <img src={CCBAppBarLogo} alt="Logo" className="small-screen-logo" />
          
          {/* Back to Accounts Button removed from here */}
          <div style={{ flex: 1 }}></div>
          <div className="connect-profile-section">
            <AccountCircle
              className="user-logo-connect"
              onClick={handleProfileClick}
              style={{ cursor: "pointer" }}
            />
            <Typography
              variant="h6"
              className="connectnavbarstyle"
              onClick={handleProfileClick}
              style={{ cursor: "pointer" }}
            >
              {username}
            </Typography>
            <IconButton onClick={handleProfileClick} color="inherit">
              <ExpandMore className="connectnavexpandbtn" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              {showProfileDetails ? (
                <div style={{ padding: "10px", minWidth: "200px" }}>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <span style={{ fontWeight: "bold" }}>Username:</span>
                    <span>{username}</span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginTop: "5px" }}>
                    <span style={{ fontWeight: "bold" }}>Account:</span>
                    <span>{awsAccount}</span>
                  </div>
                  <Button onClick={() => setShowProfileDetails(false)} sx={{ mt: 1 }}>
                    Back
                  </Button>
                </div>
              ) : (
                <>
                  <MenuItem onClick={() => setShowProfileDetails(true)}>My Profile</MenuItem>
                  <MenuItem onClick={handleLogOut}>Logout</MenuItem>
                </>
              )}
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      {/* ===== PAGE CONTENT ===== */}
      <div className="il-page-wrapper">

        {/* Row 1: Back button (left), Title (center), Empty (right) */}
        <div className="il-top-row">
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Button
              className="il-back-btn"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/aws-AccountsList-page")}
            >
              Back to Accounts
            </Button>
          </div>
          <h2 className="il-title" style={{ flex: 1, textAlign: 'center' }}>Instances</h2>
          <div style={{ flex: 1 }}></div>
        </div>

        {/* Row 2: Account (left) + Search (right) */}
        <div className="il-sub-row" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <p className="il-subtitle" style={{ textAlign: 'left', margin: 0 }}>
            Account:&nbsp;<strong>{awsAccount}</strong>
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <TextField
              placeholder="Search by alias or instance ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 17, color: "#687078" }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: "#fff",
                  borderRadius: "4px",
                  fontSize: "14px",
                  height: "36px",
                  "& fieldset": { borderColor: "#d1d5db" },
                  "&:hover fieldset": { borderColor: "#687078" },
                  "&.Mui-focused fieldset": { borderColor: "#0073bb", borderWidth: "2px" },
                },
              }}
            />
          </div>
        </div>

        {/* ── Instances Table ── */}
        {filtered.length > 0 ? (
          <div className="il-table-wrapper">
            <table className="il-table">
              <thead>
                <tr>
                  <th className="il-th">Instance Alias</th>
                  <th className="il-th">Status</th>
                  <th className="il-th">Created Time</th>
                  <th className="il-th" style={{ width: "50px" }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inst) => (
                  <tr
                    key={inst.InstanceId}
                    className={`il-tr ${loadingInstanceId === inst.InstanceId ? "il-tr-loading" : ""}`}
                    onClick={() => handleSelectInstance(inst)}
                  >
                    <td className="il-td">
                      <span className="il-alias">{inst.InstanceAlias || "—"}</span>
                      <span className="il-id">{inst.InstanceId}</span>
                    </td>
                    <td className="il-td">
                      <span className={`il-badge ${inst.Status === "ACTIVE" ? "il-badge--active" : "il-badge--inactive"}`}>
                        {inst.Status || "UNKNOWN"}
                      </span>
                    </td>
                    <td className="il-td il-td-date">{formatDate(inst.CreatedTime)}</td>
                    <td className="il-td il-td-loading" style={{ textAlign: "center", width: "50px" }}>
                      {loadingInstanceId === inst.InstanceId && (
                        <CircularProgress size={20} sx={{ color: "#0073bb" }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="il-empty">
            {instances.length === 0
              ? "No instances found for this account."
              : "No instances match your search."}
          </div>
        )}
      </div>
    </>
  );
};

export default InstanceListPage;